import { CategoryId_Schema } from "@/services/collection/categories/categoriesSchema";
import { ApiResponse_ImageNoId_Schema } from "@/services/images";
import { MarketplaceItemId_Schema } from "@/services/marketplace/item/marketplaceItemSchema";
import { ParameterElastic_Schema } from "@/services/parameters/parametersSchema";
import { PriceNumeric_Schema } from "@/services/prices/priceSchema";
import { Theme_Schema } from "@/services/themes/themeSchema";
import { NaturalNumber_Schema } from "@/utilities/schema";
import { z } from "zod";

export const OrderId_Schema = NaturalNumber_Schema;

export enum OrderStatus {
  Created = "created",
  Paid = "paid",
  Packaging = "in_transit",
  Sent = "sent",
  WaitingForConfirmation = "waiting_for_confirmation",
  WaitingForPayout = "waiting_for_payout",
  Finished = "done",
  CancelledRefundPending = "cancelled_refund_pending", // Cancelled - before the refund has been processed
  Cancelled = "cancelled", // Cancelled - the refund was already processed
  UnderDispute = "under_dispute",
}

export const OrderStatus_Schema = z.enum(OrderStatus);

export const OrderProductId_Schema = NaturalNumber_Schema;

// A fresh GoPay session for an unpaid order — only the gateway URL is of use to the FE
export const RetryPayment_Schema = z
  .object({
    paymentInfo: z.object({
      paymentUrl: z.url(), // absolute URL
    }),
  })
  .transform((response) => response.paymentInfo.paymentUrl);

export const OrderProduct_Schema = z
  .object({
    id: OrderProductId_Schema,
    category: CategoryId_Schema,
    images: ApiResponse_ImageNoId_Schema.array(),
    quantity: NaturalNumber_Schema,
    discount: PriceNumeric_Schema.nullable(),
    price: PriceNumeric_Schema, // With applied discount
    priceOriginal: PriceNumeric_Schema, // Without applied discount
    name: z.string(),
    marketPlaceItemId: MarketplaceItemId_Schema,
    // The ordered collection item as indexed in ElasticSearch — carries the theme and the already
    // merged parameters (the item's own values, foil above all, override the collectible ones).
    // Null when the item is not indexed. Only the fields the order detail row renders are picked;
    // the full shape is CollectionItemElasticReadResponse.
    collectionItemElastic: z
      .object({
        theme: Theme_Schema.nullable().optional(),
        // API may send an object (empty or keyed by string) instead of an array — normalize to array.
        parameters: z.preprocess((val) => (val && typeof val === "object" && !Array.isArray(val) ? Object.values(val) : val), ParameterElastic_Schema.array()).optional(),
      })
      .nullable()
      .optional(),
  })
  .transform(({ category, marketPlaceItemId, id, images, quantity, price, priceOriginal, name, discount, collectionItemElastic }) => ({
    id,
    categoryId: category,
    name,
    images,
    quantity,
    priceAfterDiscount: price,
    priceBeforeDiscount: priceOriginal,
    marketplaceItemId: marketPlaceItemId,
    discount,
    theme: collectionItemElastic?.theme ?? null,
    parameters: collectionItemElastic?.parameters ?? [],
  }));
